import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';
import { getAPIUrl } from '../api/apiUtils';
import { regionsStore } from '../stores';
import { groupIdToTitle } from '../utils/groupTitles';

// Сопоставление английских лейблов с русскими
export const LABEL_TRANSLATIONS = {
  "education": "Образование",
  "human interest": "Человеческие истории",
  "society": "Общество",
  "sport": "Спорт",
  "crime, law and justice": "Преступность и закон",
  "disaster, accident and emergency incident": "Катастрофы и происшествия",
  "arts, culture, entertainment and media": "Культура и искусство",
  "politics": "Политика",
  "economy, business and finance": "Экономика и бизнес",
  "lifestyle and leisure": "Образ жизни и досуг",
  "science and technology": "Наука и технологии",
  "health": "Здоровье",
  "labour": "Труд",
  "religion": "Религия",
  "weather": "Погода",
  "environment": "Экология",
  "conflict, war and peace": "Конфликты и война"
};

export const ALL_LABELS = [
  'education',
  'human interest',
  'society',
  'sport',
  'crime, law and justice',
  'disaster, accident and emergency incident',
  'arts, culture, entertainment and media',
  'politics',
  'economy, business and finance',
  'lifestyle and leisure',
  'science and technology',
  'health',
  'labour',
  'religion',
  'weather',
  'environment',
  'conflict, war and peace'
];

const DEFAULT_LABELS = ['conflict, war and peace', 'politics'];

class PredictionTrendsStore {
  groups = [];
  loading = false;
  error = null;
  statsByGroup = [];
  loadedData = [];
  labels = ALL_LABELS.slice();
  labelColors = {};
  sortAsc = true;
  selectedLabels = DEFAULT_LABELS.slice();
  filters = {};
  selectedGroups = [];
  startDate = '';
  endDate = '';
  interval = 7;
  displayMode = 'sum';
  singleLabel = '';
  lastQueryEndDate = undefined
  lastSelectedGroups = []
  lastQueryGlobalStart = undefined
  lastQueryGlobalEnd = undefined
  lastQueryDisplayMode = undefined
  lastQueryInterval = undefined
  needTransrofm = false

  // приватные поля для синка
  _filtersKey = 'globalFilters';
  _lastFiltersJSON = '';
  _watcherId = null;
  _suspendWatchOnce = false; // чтобы не ловить собственную запись в localStorage, если нужно

  constructor() {
    makeAutoObservable(this);
    this.init();

    // Подписка на кросс-вкладки (стрельнёт только в других вкладках).
    // Данные прогнозов загружает только смонтированный PredictionTrendsPage;
    // сам глобальный store не должен запускать запросы на других страницах.
    window.addEventListener('storage', (e) => {
      if (e.key === this._filtersKey) {
        this._applyFiltersFromLocalStorage();
      }
    });

    // Вотчер для той же вкладки (polling, лёгкий и надёжный)
    this._startLocalStorageWatcher();

    // Моментальный апдейт по своему кастомному событию
    window.addEventListener('globalFilters:changed', () => {
      this._applyFiltersFromLocalStorage();
    });

    // Чистка
    window.addEventListener('beforeunload', () => {
      if (this._watcherId) clearInterval(this._watcherId);
    });
  }

  init() {
    this.loadGroups();
    this._applyFiltersFromLocalStorage(); // инициализация из LS
  }

  // === Синхронизация с localStorage ===
  _readFiltersJSON() {
    return localStorage.getItem(this._filtersKey) || '';
  }

  _applyFiltersFromLocalStorage() {
    const json = this._readFiltersJSON();
    if (json === this._lastFiltersJSON) return; // нечего обновлять
    let parsed = {};
    try { parsed = json ? JSON.parse(json) : {}; } catch { parsed = {}; }

    runInAction(() => {
      this.filters = parsed;
      this.selectedGroups = parsed.selectedGroups || [];
      this.startDate = parsed.startDate || '';
      this.endDate = parsed.endDate || '';
    });

    this._lastFiltersJSON = json; // важно обновить «снимок»
    // fetchStats не вызываем здесь напрямую — это сделает reaction
  }

  _startLocalStorageWatcher() {
    this._lastFiltersJSON = this._readFiltersJSON();
    this._watcherId = setInterval(() => {
      if (this._suspendWatchOnce) { // если только что сами записали
        this._suspendWatchOnce = false;
        return;
      }
      const curr = this._readFiltersJSON();
      if (curr !== this._lastFiltersJSON) {
        this._applyFiltersFromLocalStorage();
      }
    }, 800);
  }

  // Единая точка записи фильтров в LS + обновление стора
  setFilters(newFilters) {
    const json = JSON.stringify(newFilters || {});
    // сначала обновляем стор (реакция сама перезапросит данные)
    runInAction(() => {
      this.filters = newFilters || {};
      this.selectedGroups = this.filters.selectedGroups || [];
      this.startDate = this.filters.startDate || '';
      this.endDate = this.filters.endDate || '';
    });

    // затем пишем в LS и обновляем снапшот
    this._suspendWatchOnce = true; // чтобы поллер не дёрнулся тут же
    localStorage.setItem(this._filtersKey, json);
    this._lastFiltersJSON = json;

    // (опционально) бросаем кастомное событие для мгновенных подписчиков
    window.dispatchEvent(new Event('globalFilters:changed'));
  }

  setInterval(val) { this.interval = val; }
  setDisplayMode(val) { this.displayMode = val;
    
    if ((val === 'split' || val === 'compare') && this.singleLabel === ""){
      this.singleLabel = ALL_LABELS[0]
    }    
   }
  setSingleLabel(val) { this.singleLabel = val; }

  // Если эти методы вызываются из UI — они тоже синкают LS через setFilters:
  setSelectedGroups(val) { this.setFilters({ ...this.filters, selectedGroups: val }); }
  setStartDate(val) { this.setFilters({ ...this.filters, startDate: val }); }
  setEndDate(val) { this.setFilters({ ...this.filters, endDate: val }); }

  setSelectedLabels(val) { this.selectedLabels = val; }
  setSortAsc(val) { this.sortAsc = val; }
  setLabels(val) { this.labels = val; }

  // Загружаем только посты выбранной линии: её тематику и, если нужно, группу.
  async buildModalRowsForInterval(intervalLabel, { topicLabel, groupId } = {}) {
    if (!intervalLabel || !topicLabel) return [];
    const match = intervalLabel.match(/^(\d{2})\.(\d{2})\.(\d{4}) - (\d{2})\.(\d{2})\.(\d{4})$/);
    if (!match) return [];
    const startDate = `${match[3]}-${match[2]}-${match[1]}`;
    const endDate = `${match[6]}-${match[5]}-${match[4]}`;
    const groups = groupId === undefined
      ? this.selectedGroups
      : this.selectedGroups.filter(id => String(id) === String(groupId));

    const results = await Promise.all(groups.map(id =>
      axios.get(`${getAPIUrl()}/vk/group_prediction_posts`, {
        params: { group_id: id, start_date: startDate, end_date: endDate, label: topicLabel }
      })
    ));
    return results.flatMap(response => response.data).map(post => {
      const vkGroupId = -Math.abs(Number(post.group_id));
      return {
        'Группа': groupIdToTitle[String(post.group_id)] || post.group_id,
        'Дата': post.date,
        'ID поста': post.post_id,
        'Текст': post.text,
        'Комментарии и ответы': post.comment_count,
        'Уверенность модели': post.score,
        'Ссылка на пост': `https://vk.com/wall${vkGroupId}_${post.post_id}`,
      };
    });
  }

  get sortedGroups() {
    return [...this.groups].sort((a, b) => {
      if (this.sortAsc) return a.name.localeCompare(b.name, 'ru');
      return b.name.localeCompare(a.name, 'ru');
    });
  }

  getColor(label) {
    if (this.labelColors[label]) return this.labelColors[label];
    const hash = label.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    const color = `hsl(${hash % 360}, 60%, 60%)`;
    this.labelColors[label] = color;
    return color;
  }

  getGroupColor(groupId) {
    const str = String(groupId);
    const hash = str.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return `hsl(${(hash * 47) % 360}, 70%, 50%)`;
  }

  async loadGroups() {
    try {
      await regionsStore.fetchRegions();
      const allGroups = regionsStore.allGroups.map(g => ({
        id: String(g.group_id),
        name: g.title
      }));
      runInAction(() => { this.groups = allGroups; });
    } catch (e) {
      runInAction(() => { this.error = e.message; });
    }
  }

  async fetchStats(start = 0,end = 100) {  
    if (this.lastQueryGlobalStart!= this.startDate ||
        this.lastQueryGlobalEnd!= this.endDate ||
        this.lastQueryInterval!= this.interval ||
        this.lastSelectedGroups!= this.selectedGroups)
    {
      this.loadedData = []
      this.statsByGroup = []
      this.lastQueryEndDate = undefined
      this.loading = true
    }    

    if (this.lastQueryDisplayMode!= this.displayMode)
    {
      this.statsByGroup = []
      this.loading = true
      this.needTransrofm = true
    }    
    if ((this.displayMode === 'split' || this.displayMode === 'compare') && !this.singleLabel) {
      runInAction(() => {
        this.singleLabel = ALL_LABELS[0];
      });
    }    
    if (!this.startDate || !this.endDate) {
      console.warn('No start or end date provided');
      return;
  }

  // Преобразуем в Date
  const baseStartDate = new Date(this.startDate);
  const baseEndDate = new Date(this.endDate);

  if (isNaN(baseStartDate.getTime()) || isNaN(baseEndDate.getTime())) {
      console.error('Invalid start or end date:', this.startDate, this.endDate);
      return;
  } // работает и со строкой, и с Date    

    let startDate = new Date(baseStartDate);
    startDate.setDate(startDate.getDate() + start);

    let endDate = new Date(baseStartDate);
    endDate.setDate(endDate.getDate() + (end+50) * this.interval);
    if (endDate.getTime() > baseEndDate.getTime())
      endDate = new Date(baseEndDate)
    

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Месяцы с 0
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
  };
  
  startDate = formatDate(startDate);
  endDate = formatDate(endDate);
          
      try {        
        if (!this.lastQueryEndDate || this.lastQueryEndDate<endDate )
        {console.log('params: ', '\nstart_date: ', this.lastQueryEndDate ? this.lastQueryEndDate : startDate,
          '\nend_date: ', endDate, "\nintertval: ", this.interval
        )
        const promises = this.selectedGroups.map(gid =>
          axios.get(`${getAPIUrl()}/vk/group_prediction_stats`, {
            params: {
              group_id: gid,
              start_date: this.lastQueryEndDate ? this.lastQueryEndDate : startDate,
              end_date: endDate,
              interval: this.interval
            }
          }).then(response => ({
            group_id: gid,        // ← ДОБАВЛЯЕМ ВРУЧНУЮ
            data: response.data   // ← данные от API
          }))
        );
        
        const results = await Promise.all(promises); 

        console.log("results: ",results)

        if (this.loadedData.length !== 0) {
          // Для каждого нового результата находим соответствующий старый по group_id и мержим данные
          results.forEach(newResult => {
            const existingResultIndex = this.loadedData.findIndex(
              oldResult => oldResult.group_id === newResult.group_id
            );
        
            if (existingResultIndex !== -1) {
              // Получаем существующие интервалы как Set для быстрой проверки
              const existingIntervals = new Set(
                this.loadedData[existingResultIndex].data.map(item => item.interval)
              );
        
              // Фильтруем только новые интервалы, которых ещё нет
              const newIntervals = newResult.data.filter(
                newItem => !existingIntervals.has(newItem.interval)
              );
        
              // Добавляем новые интервалы к существующим данным
              this.loadedData[existingResultIndex].data.push(...newIntervals);              
              
            } else {
              // Если группа ещё не загружалась — добавляем целиком
              this.loadedData.push(newResult);
            }
          });
        } else {
          this.loadedData = results;
        }}
        if(!this.lastQueryEndDate || this.lastQueryEndDate < endDate){
          this.lastQueryEndDate =  endDate
        }
           
        this.lastSelectedGroups = this.selectedGroups
        this.lastQueryGlobalStart = this.startDate
        this.lastQueryGlobalEnd = this.endDate
        this.lastQueryDisplayMode = this.displayMode    
        this.lastQueryInterval = this.interval
        if (this.displayMode === 'sum'){
                
          const transformedResults = (() => {
            // Собираем все уникальные маркеры, которые есть в this.selectedLabels
            const markers = new Set();
            this.loadedData.forEach(result => {
              result.data.forEach(item => {
                Object.keys(item.counts).forEach(marker => {
                  if (this.selectedLabels.includes(marker)) {
                    markers.add(marker);
                  }
                });
              });
            });
          
            // Преобразуем данные
            return Array.from(markers).map(marker => {
              // Создаем объект для хранения значений по интервалам
              const intervalValues = new Map();
          
              // Проходим по всем результатам и интервалам
              this.loadedData.forEach(result => {
                result.data.forEach(item => {
                  const { interval, counts, comment_counts } = item;
                  const point = intervalValues.get(interval) || { value: 0, commentCount: 0 };
                  point.value += counts[marker] || 0;
                  point.commentCount += comment_counts?.[marker] || 0;
                  intervalValues.set(interval, point);
                });
              });
          
              // Преобразуем Map в массив объектов { date, value }
              const data = Array.from(intervalValues, ([interval, point]) => ({
                date: interval,
                ...point
              }));
          
              return {
                marker: LABEL_TRANSLATIONS[marker],
                topicLabel: marker,
                data
              };
            });
          })();                          
          
          runInAction(() => {
            this.statsByGroup = transformedResults;
            // this.labels = Array.from(allLabels);
          });
        }
        else{          
          const transformedResults = this.loadedData.map(({ group_id, data }) => {
            const intervalValues = data.map(item => {
              // если нужен один конкретный label
              if (this.singleLabel) {
                return {
                  date: item.interval,
                  value: item.counts[this.singleLabel] || 0,
                  commentCount: item.comment_counts?.[this.singleLabel] || 0
                };
              }
        
              // если выбран список меток — суммируем их
              const sum = this.selectedLabels.reduce((acc, label) => {
                return acc + (item.counts[label] || 0);
              }, 0);
              const commentCount = this.selectedLabels.reduce((acc, label) => {
                return acc + (item.comment_counts?.[label] || 0);
              }, 0);
        
              return {
                date: item.interval,
                value: sum,
                commentCount
              };
            });
        
            return {            
              marker: groupIdToTitle[group_id],
              groupId: group_id,
              topicLabel: this.singleLabel,
              topicTitle: LABEL_TRANSLATIONS[this.singleLabel] || this.singleLabel,
              data: intervalValues
            };
          });              
        
          runInAction(() => {
            this.statsByGroup = transformedResults;
          });
        }   
      
      
      } catch (e) {
        runInAction(() => {
          this.error = e.response?.data?.error || e.message;
        });
      } finally {
        runInAction(() => { this.loading = false; });      
      }
    
  }
}

export const predictionTrendsStore = new PredictionTrendsStore();
